/**
 * WizardContainer Component
 *
 * Main container for the device updater wizard. Orchestrates the wizard flow,
 * renders the appropriate step component, and handles navigation.
 * Uses PopupCard for consistent UI with other popups in the application.
 */

import { Button, CloseButton } from '@ui/shared/Button';
import * as React from 'react';
import styled from 'styled-components';
import { KEYS } from '../../../../keys';
import { CardBody } from '../../../shared/card/CardBody';
import { CardFooter } from '../../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../../shared/card/CardHeader';
import { Icon } from '../../../shared/Icon';
import {
  InnerPopupCard,
  PopupCardContent,
} from '../../../shared/popups/PopupCard';
import { topLevelPopup } from '../../../../data/providers/popupsProvider';
import {
  UpdaterServicesProvider,
  useUpdaterServices,
} from '../hooks/useUpdaterServices';
import { WizardProvider, useWizard } from '../hooks/useWizard';
import { WizardStep, UpdaterError } from '../types/wizard-types';
import { DevToolbar } from './DevToolbar';
import { EIDConfirmationPopup } from './EIDConfirmationPopup';
import { ErrorRecoveryPanel } from './ErrorRecoveryPanel';
import { UnsupportedBrowserScreen } from './UnsupportedBrowserScreen';
import { WizardStepper } from './WizardStepper';

// Step components
import { BootloaderTransferStep } from './steps/BootloaderTransferStep';
import { DeviceConnectionStep } from './steps/DeviceConnectionStep';
import { EasyInstallerStep } from './steps/EasyInstallerStep';
import { EIDEntryStep } from './steps/EIDEntryStep';
import { FinalizationStep } from './steps/FinalizationStep';
import { FirmwareFlashingStep } from './steps/FirmwareFlashingStep';

// Declare ENVIRONMENT variable (set by webpack)
declare const ENVIRONMENT: string;

/**
 * Check if we're in development mode
 */
const isDevelopment = (): boolean => {
  // Check build-time environment
  if (typeof ENVIRONMENT !== 'undefined' && ENVIRONMENT !== 'production') {
    return true;
  }

  // Check runtime hostname
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === 'cloud.dev.hawkindynamics.com'
    );
  }

  return false;
};

/**
 * Check if WebHID is supported in this browser
 */
const isWebHIDSupported = (): boolean => {
  return 'hid' in navigator;
};

/**
 * Props for WizardContainer
 */
export interface WizardContainerProps {
  /** Callback when popup is closed */
  onClose: () => void;
  /** Callback when wizard completes successfully */
  onComplete?: () => void;
  /** Initial step to start at (dev mode only) */
  initialStep?: WizardStep;
}

/**
 * Inner wizard content (uses context)
 */
const WizardContent: React.FC<WizardContainerProps> = ({
  onClose,
  onComplete,
}: WizardContainerProps): React.ReactElement => {
  const { state, nextStep, previousStep, reset, setError } = useWizard();
  const { disconnectDevice, fetchFirmware, logger } = useUpdaterServices();
  const isDevMode = isDevelopment();
  const [isCheckingFirmware, setIsCheckingFirmware] = React.useState(false);

  /**
   * Handle close - disconnect device and call onClose
   */
  const handleClose = React.useCallback(async (): Promise<void> => {
    await disconnectDevice();
    onClose();
  }, [disconnectDevice, onClose]);

  /**
   * Proceed after EID confirmation - checks firmware availability and moves to next step
   */
  const proceedAfterEIDConfirmation =
    React.useCallback(async (): Promise<void> => {
      setIsCheckingFirmware(true);
      try {
        await fetchFirmware();
        // Firmware fetched successfully, close popup and proceed to next step
        topLevelPopup.next(null);
        nextStep();
      } catch (err) {
        // Close popup before showing error
        topLevelPopup.next(null);
        // Error already handled by firmware service - it throws UpdaterError
        if (err instanceof UpdaterError) {
          setError(err);
        } else {
          // Fallback for unexpected errors
          logger.error('Firmware availability check failed:', err);
        }
      } finally {
        setIsCheckingFirmware(false);
      }
    }, [fetchFirmware, nextStep, setError, logger]);

  /**
   * Close the EID confirmation popup
   */
  const closeEIDConfirmation = React.useCallback((): void => {
    topLevelPopup.next(null);
  }, []);

  /**
   * Show the EID confirmation popup
   */
  const showEIDConfirmation = React.useCallback((): void => {
    topLevelPopup.next(
      <EIDConfirmationPopup
        eid={state.eid || ''}
        onConfirm={proceedAfterEIDConfirmation}
        onCancel={closeEIDConfirmation}
      />
    );
  }, [state.eid, proceedAfterEIDConfirmation, closeEIDConfirmation]);

  /**
   * Handle continue - shows confirmation on EID step, then checks firmware availability
   */
  const handleContinue = React.useCallback(async (): Promise<void> => {
    // On EID step, show confirmation popup before proceeding
    if (state.currentStep === WizardStep.EID_ENTRY) {
      showEIDConfirmation();
    } else {
      // Not on EID step, just proceed
      nextStep();
    }
  }, [state.currentStep, showEIDConfirmation, nextStep]);

  // Handle Enter key to trigger Continue button
  React.useEffect((): (() => void) => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (
        e.key === 'Enter' &&
        state.canProceed &&
        !state.error &&
        !state.completed &&
        !isCheckingFirmware
      ) {
        e.preventDefault();
        handleContinue();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return (): void => window.removeEventListener('keydown', handleKeyDown);
  }, [
    state.canProceed,
    state.error,
    state.completed,
    isCheckingFirmware,
    handleContinue,
  ]);

  /**
   * Render the current step component
   */
  const renderCurrentStep = (): React.ReactElement => {
    // If there's an error, show the error panel
    if (state.error) {
      return <ErrorRecoveryPanel error={state.error} />;
    }

    switch (state.currentStep) {
      case WizardStep.EID_ENTRY:
        return <EIDEntryStep />;
      case WizardStep.DEVICE_CONNECTION:
        return <DeviceConnectionStep />;
      case WizardStep.BOOTLOADER_TRANSFER:
        return <BootloaderTransferStep />;
      case WizardStep.EASY_INSTALLER_LOADING:
        return <EasyInstallerStep />;
      case WizardStep.FIRMWARE_FLASHING:
        return <FirmwareFlashingStep />;
      case WizardStep.FINALIZATION:
        return (
          <FinalizationStep onComplete={onComplete} onClose={handleClose} />
        );
      default:
        return <EIDEntryStep />;
    }
  };

  /**
   * Get step title with number prefix
   */
  const getStepTitle = (): string => {
    switch (state.currentStep) {
      case WizardStep.EID_ENTRY:
        return '1. Get Started';
      case WizardStep.DEVICE_CONNECTION:
        return '2. Connect Plates';
      case WizardStep.BOOTLOADER_TRANSFER:
        return '3. Update in Progress';
      case WizardStep.EASY_INSTALLER_LOADING:
        return '4. Update in Progress';
      case WizardStep.FIRMWARE_FLASHING:
        return '5. Update in Progress';
      case WizardStep.FINALIZATION:
        return 'Update Complete';
      default:
        return 'Device Updater';
    }
  };

  /**
   * Handle start over click
   */
  const handleStartOver = (): void => {
    reset();
  };

  /**
   * Determine automated or final step
   */
  const stepType = (): string => {
    if (
      [
        WizardStep.BOOTLOADER_TRANSFER,
        WizardStep.EASY_INSTALLER_LOADING,
        WizardStep.FIRMWARE_FLASHING,
      ].includes(state.currentStep)
    ) {
      return 'isAutomated';
    } else if (state.currentStep === WizardStep.FINALIZATION) {
      return 'isFinal';
    }
  };

  return (
    <InnerPopupCard onClose={handleClose} size='large' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <TitleSection>
              <Icon name='cloud_download' size='medium' />
              <HeaderTitle>Update your Hawkin Force Plates</HeaderTitle>
            </TitleSection>
          </CardHeaderLeft>
          <CardHeaderRight>
            {(stepType() !== 'isAutomated' || state.error) &&
              !state.completed && (
                <CloseButton
                  onClick={handleClose}
                  data-testid={KEYS.WIZARD_CLOSE_BUTTON}
                />
              )}
          </CardHeaderRight>
        </CardHeader>

        <WizardStepper />

        <StepHeader>
          <StepTitle>{getStepTitle()}</StepTitle>
          {state.eid && state.currentStep !== WizardStep.EID_ENTRY && (
            <EIDDisplay>
              <Icon name='qr_code' size='tiny' />
              <span>{state.eid}</span>
            </EIDDisplay>
          )}
        </StepHeader>

        <StyledCardBody>{renderCurrentStep()}</StyledCardBody>

        {state.error && (
          <CardFooter surface={8}>
            <FooterContent>
              <LeftActions />
              <RightActions>
                <Button
                  title='Close'
                  flavor='secondary'
                  onClick={handleClose}
                />
              </RightActions>
            </FooterContent>
          </CardFooter>
        )}

        {!state.error && stepType() !== 'isFinal' && (
          <CardFooter surface={8}>
            <FooterContent>
              <LeftActions>
                {state.canGoBack && (
                  <Button
                    icon='arrow_back'
                    title='Back'
                    flavor='none'
                    onClick={previousStep}
                    disabled={stepType() === 'isAutomated'}
                    data-testid={KEYS.WIZARD_BACK_BUTTON}
                  />
                )}
                {state.currentStep !== WizardStep.EID_ENTRY && (
                  <Button
                    icon='restart_alt'
                    title='Start Over'
                    flavor='none'
                    onClick={handleStartOver}
                    disabled={stepType() === 'isAutomated'}
                    data-testid={KEYS.WIZARD_START_OVER_BUTTON}
                  />
                )}
              </LeftActions>
              <RightActions>
                {stepType() !== 'isAutomated' && (
                  <Button
                    title='Continue'
                    flavor='primary'
                    disabled={!state.canProceed || isCheckingFirmware}
                    onClick={handleContinue}
                    data-testid={KEYS.WIZARD_CONTINUE_BUTTON}
                  />
                )}
              </RightActions>
            </FooterContent>
          </CardFooter>
        )}
      </PopupCardContent>

      {/* Development toolbar for testing */}
      <DevToolbar enabled={isDevMode} />
    </InnerPopupCard>
  );
};

/**
 * WizardContainer component (with providers)
 */
export const WizardContainer: React.FC<WizardContainerProps> = (
  props: WizardContainerProps
): React.ReactElement => {
  // Check for WebHID support before rendering the wizard
  if (!isWebHIDSupported()) {
    return <UnsupportedBrowserScreen onClose={props.onClose} />;
  }

  return (
    <UpdaterServicesProvider>
      <WizardProvider initialStep={props.initialStep}>
        <WizardContent {...props} />
      </WizardProvider>
    </UpdaterServicesProvider>
  );
};

// Styled Components

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
`;

const HeaderTitle = styled.h5`
  margin: 0;
  font-weight: bold;
  color: var(--color-text);
`;

const StepHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  margin-bottom: 8px;
`;

const StepTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text);
`;

const EIDDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background: var(--color-surface-2);
  border-radius: 16px;
  font-size: 12px;
  font-family: monospace;
  color: var(--color-disabled-text);
`;

const StyledCardBody = styled(CardBody)`
  flex: 1;
  padding: 20px;
`;

const FooterContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const LeftActions = styled.div`
  display: flex;
  gap: 10px;
`;

const RightActions = styled.div`
  display: flex;
  gap: 10px;
`;

export default WizardContainer;
